import { isSuccessStatus, isFailedStatus } from './assessRecovery';

export const MAX_WINDOWS_PER_POLL = 96;

export type CoverageGap = {
  id: string;
  from: string;
  to: string;
  durationMs: number;
  pollsMissed: number;
};

export type CoverageSegment = {
  kind: 'covered' | 'missed';
  from: number;
  to: number;
  leftPct: number;
  widthPct: number;
  gap?: CoverageGap;
};

export function formatSpan(ms: number): string {
  if (ms < 60_000) return 'under a minute';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 120) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

export function catchUpEstimate(
  durationMs: number,
  pollIntervalSeconds: number | null,
): { polls: number; wallMs: number } {
  const intervalMs = Math.max(1, (pollIntervalSeconds || 60) * 1000);
  const windows = Math.max(1, Math.ceil(durationMs / intervalMs));
  const polls = Math.ceil(windows / MAX_WINDOWS_PER_POLL);
  return { polls, wallMs: polls * intervalMs };
}

function minGapMs(pollIntervalSeconds: number | null): number {
  return Math.max(((pollIntervalSeconds || 0) * 1000) * 3, 15 * 60 * 1000);
}

export function findCoverageGaps(
  runs: Array<{ startDate?: string; status?: string }>,
  options: {
    rangeStart: number;
    rangeEnd: number;
    pollIntervalSeconds: number | null;
  },
): CoverageGap[] {
  const start = options.rangeStart;
  const end = options.rangeEnd;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

  const intervalMs = Math.max(1, (options.pollIntervalSeconds || 60) * 1000);
  const quiet = minGapMs(options.pollIntervalSeconds);
  const hits = runs
    .filter((run) => isSuccessStatus(run.status))
    .map((run) => Date.parse(run.startDate || ''))
    .filter((time) => Number.isFinite(time) && time >= start && time <= end)
    .sort((a, b) => a - b);

  const gaps: CoverageGap[] = [];
  let cursor = start;
  const pushGap = (from: number, to: number) => {
    const durationMs = to - from;
    if (durationMs < quiet) return;
    const id = `${new Date(from).toISOString()}_${new Date(to).toISOString()}`;
    gaps.push({
      id,
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
      durationMs,
      pollsMissed: Math.max(1, Math.floor(durationMs / intervalMs)),
    });
  };

  for (const time of hits) {
    if (time - cursor >= quiet) pushGap(cursor, time);
    cursor = Math.max(cursor, time + intervalMs);
  }
  if (end - cursor >= quiet) pushGap(cursor, end);
  return gaps;
}

export function coverageSegments(
  gaps: CoverageGap[],
  rangeStart: number,
  rangeEnd: number,
): CoverageSegment[] {
  const span = rangeEnd - rangeStart;
  if (!(span > 0)) return [];
  const pct = (ms: number) => ((ms - rangeStart) / span) * 100;
  const width = (from: number, to: number) => ((to - from) / span) * 100;
  const segments: CoverageSegment[] = [];
  let cursor = rangeStart;
  for (const gap of gaps) {
    const from = Date.parse(gap.from);
    const to = Date.parse(gap.to);
    if (from > cursor) {
      segments.push({
        kind: 'covered',
        from: cursor,
        to: from,
        leftPct: pct(cursor),
        widthPct: width(cursor, from),
      });
    }
    segments.push({
      kind: 'missed',
      from,
      to,
      leftPct: pct(from),
      widthPct: width(from, to),
      gap,
    });
    cursor = to;
  }
  if (cursor < rangeEnd) {
    segments.push({
      kind: 'covered',
      from: cursor,
      to: rangeEnd,
      leftPct: pct(cursor),
      widthPct: width(cursor, rangeEnd),
    });
  }
  return segments;
}

export function failedRunMarks(
  runs: Array<{ startDate?: string; status?: string }>,
  rangeStart: number,
  rangeEnd: number,
): Array<{ at: number; leftPct: number }> {
  const span = rangeEnd - rangeStart;
  if (!(span > 0)) return [];
  return runs
    .filter((run) => isFailedStatus(run.status))
    .map((run) => Date.parse(run.startDate || ''))
    .filter((time) => Number.isFinite(time) && time >= rangeStart && time <= rangeEnd)
    .map((at) => ({ at, leftPct: ((at - rangeStart) / span) * 100 }));
}

export function timelineRange(args: {
  dateStart?: string | null;
  dateEnd?: string | null;
  lastSuccessAt?: string | null;
  now?: number;
}): { start: number; end: number } {
  const now = args.now ?? Date.now();
  const endRaw = args.dateEnd ? Date.parse(args.dateEnd) : NaN;
  const end = Number.isFinite(endRaw) && endRaw < now - 60_000 ? endRaw : now;
  const startRaw = args.dateStart ? Date.parse(args.dateStart) : NaN;
  let start = Number.isFinite(startRaw) ? startRaw : end - 24 * 60 * 60 * 1000;
  const last = args.lastSuccessAt ? Date.parse(args.lastSuccessAt) : NaN;
  if (Number.isFinite(last) && last < start) start = last;
  if (end - start > 14 * 24 * 60 * 60 * 1000) start = end - 14 * 24 * 60 * 60 * 1000;
  if (start >= end) start = end - 60 * 60 * 1000;
  return { start, end };
}
